import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { FilterBar, FilterField, Input, RadixSelect } from '@/components/ui';
import type { VendorApplicationStatusEnum } from '@/lib/api-admin/types';
import { useVendorFiltersStore } from '@/stores/useVendorFiltersStore';

const DEBOUNCE_MS = 300;

const STATUS_FILTER_OPTIONS: ReadonlyArray<VendorApplicationStatusEnum> = [
  'SUBMITTED',
  'UNDER_REVIEW',
  'RESUBMISSION_REQUESTED',
  'APPROVED',
  'CREDENTIALS_ISSUED',
  'REJECTED',
  'WITHDRAWN',
];

export const VendorFiltersBar = () => {
  const { t } = useTranslation('vendors');
  const keyword = useVendorFiltersStore((s) => s.keyword);
  const status = useVendorFiltersStore((s) => s.status);
  const slaDueBefore = useVendorFiltersStore((s) => s.slaDueBefore);
  const setKeyword = useVendorFiltersStore((s) => s.setKeyword);
  const setStatuses = useVendorFiltersStore((s) => s.setStatuses);
  const setSlaDueBefore = useVendorFiltersStore((s) => s.setSlaDueBefore);

  const [local, setLocal] = useState(keyword);

  useEffect(() => {
    if (local === keyword) return;
    const id = setTimeout(() => setKeyword(local), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [local, keyword, setKeyword]);

  const singleStatus: '' | VendorApplicationStatusEnum =
    status.length === 1 ? (status[0] as VendorApplicationStatusEnum) : '';

  const statusOptions = [
    { value: '', label: t('vendors.filters.statusAll') },
    ...STATUS_FILTER_OPTIONS.map((s) => ({ value: s, label: t(`vendors.status.${s}`) })),
  ];

  return (
    <FilterBar
      aria-label={t('vendors.filters.label')}
      data-testid="vendor-filters"
      filters={
        <>
          <FilterField label={t('vendors.filters.keyword')}>
            <Input
              id="vendor-keyword"
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder={t('vendors.filters.keywordPlaceholder')}
              className="w-full"
              maxLength={100}
            />
          </FilterField>
          <FilterField label={t('vendors.filters.status')}>
            <RadixSelect
              id="vendor-status"
              value={singleStatus}
              onChange={(v) =>
                setStatuses(v === '' ? [] : [v as VendorApplicationStatusEnum])
              }
              options={statusOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('vendors.filters.slaDueBefore')}>
            <Input
              id="vendor-sla-due-before"
              type="date"
              value={slaDueBefore ?? ''}
              onChange={(e) => setSlaDueBefore(e.target.value || null)}
              className="w-full"
            />
          </FilterField>
        </>
      }
    />
  );
};
